import type {
  EventRepelMob,
  PlayerFireProjectile,
  ServerMobUpdateMessage,
  ServerMobUpdateStationaryMessage,
} from '@airmash/protocol';
import { GAME_TYPES, MOB_DESPAWN_TYPES, MOB_TYPES, SERVER_PACKETS } from '@airmash/protocol';
import type { Sprite } from 'pixi.js';
import { clock } from './Clock';
import { DISPLAY_DEFAULTS } from './Config';
import {
  HITSHAPES_TEAM_1_COLOR,
  HITSHAPES_TEAM_1_THRUSTER,
  HITSHAPES_TEAM_2_COLOR,
  HITSHAPES_TEAM_2_THRUSTER,
  MAP_CENTER_X,
  MAP_CENTER_Y,
  MAP_HEIGHT,
  MAP_WIDTH,
  MISSILE_TEAM_1_COLOR,
  MISSILE_TEAM_2_COLOR,
  SHADOW_SCALING,
  TWO_PI,
} from './Constants';
import { MISSILE_LOOKS, type MissileLook } from './GameData';
import {
  inScreen as graphicsInScreen,
  shadowCoords as graphicsShadowCoords,
  transform as graphicsTransform,
} from './Graphics';
import { reducedFactor as networkReducedFactor, timing as networkTiming } from './Network';
import { missileSmoke as particlesMissileSmoke } from './particles/Particles';
import { get as playersGet, getMe as playersGetMe } from './Players';
import { session } from './Session';
import { settings } from './Settings';
import {
  clearThruster as soundClearThruster,
  powerup as soundPowerup,
  updateMobThruster as soundUpdateThruster,
} from './Sound';
import type { TextureConfigName } from './Textures';
import { init as texturesInit } from './Textures';
import { clamp, converge, oscillator, rand } from './Tools';
import type { TeamId } from './types/Players';
import Vector from './Vector';

/** The mob types with crate behaviour: stationary pickups without a thruster. */
const CRATE_MOB_TYPES: ReadonlySet<MOB_TYPES> = new Set([MOB_TYPES.UPGRADE, MOB_TYPES.SHIELD, MOB_TYPES.INFERNO]);

/** The crate texture by crate type. */
const CRATE_TEXTURE_BY_MOB_TYPE: Readonly<Partial<Record<MOB_TYPES, TextureConfigName>>> = {
  [MOB_TYPES.UPGRADE]: 'crateUpgrade',
  [MOB_TYPES.SHIELD]: 'crateShield',
  [MOB_TYPES.INFERNO]: 'crateRampage',
};

interface MobState {
  inactive: boolean;
  despawnTicker: number;
  despawnType: MOB_DESPAWN_TYPES;
  baseScale: number;
  baseScaleShadow: number;
  luminosity: number;
}

function isHitshapesTheme() {
  return (settings.display?.theme ?? DISPLAY_DEFAULTS.theme) === 'hitshapes';
}

interface MobSprites {
  thrusterGlow?: Sprite;
  smokeGlow?: Sprite;
  thruster?: Sprite;
  sprite?: Sprite;
  shadow?: Sprite;
}

export class Mob {
  public id: number;
  public type: MOB_TYPES;
  public pos: Vector;
  public missile: boolean;
  public crate: boolean;
  public spriteRot = 0;
  public speed = new Vector(0, 0);
  public accel = new Vector(0, 0);
  public maxSpeed = 0;
  public exhaust = 0;
  public lastAccelX = 0;
  public lastAccelY = 0;
  public stationary: boolean = false;
  public sprites: MobSprites = {};
  public state: MobState;
  public randomness: number;
  public culled: boolean;
  public visibility: boolean;
  public reducedFactor: number | null = null;
  public forDeletion: boolean;
  public spawnTime: number;
  public lastPacket: number;
  public thrusterName = '';
  /** The missile's exhaust and thruster look; a crate has none. */
  public look: MissileLook | undefined;
  public ownerId?: number;

  constructor(
    msg: PlayerFireProjectile | ServerMobUpdateMessage | ServerMobUpdateStationaryMessage | EventRepelMob,
    ownerId?: number,
  ) {
    this.id = msg.id;
    this.type = msg.type;
    this.thrusterName = `mob_${this.id}`;
    this.pos = new Vector(msg.posX, msg.posY);
    this.look = MISSILE_LOOKS[this.type];
    this.missile = this.look !== undefined;
    this.crate = CRATE_MOB_TYPES.has(this.type);

    // Only the stationary update carries no kinematics
    if (this.look !== undefined && 'speedX' in msg) {
      this.initializeMissile(msg, this.look);
    } else {
      this.initializeStationary();
    }

    this.state = {
      inactive: false,
      despawnTicker: 0,
      despawnType: MOB_DESPAWN_TYPES.EXPIRED,
      baseScale: 1,
      baseScaleShadow: 1,
      luminosity: 1,
    };
    this.randomness = rand(0, 1e5);
    this.culled = false;
    this.visibility = true;
    this.forDeletion = false;
    this.spawnTime = clock.time;
    this.lastPacket = networkTiming.timeNetwork;
    this.ownerId = ownerId;

    this.setupSprite();
  }

  /** A moving mob: velocity, acceleration, speed cap, and the exhaust length from its look. */
  private initializeMissile(msg: PlayerFireProjectile | ServerMobUpdateMessage | EventRepelMob, look: MissileLook) {
    this.speed.set(msg.speedX, msg.speedY);
    this.accel.set(msg.accelX, msg.accelY);
    this.maxSpeed = msg.maxSpeed;
    this.exhaust = look.exhaust;
    this.lastAccelX = 0;
    this.lastAccelY = 0;
    this.stationary = false;
    this.spriteRot = this.speed.angle() + Math.PI;
  }

  private initializeStationary() {
    this.stationary = true;
  }

  setupSprite() {
    if (this.missile) {
      this.setupMissileThrusterSprites();
    }

    switch (this.type) {
      case MOB_TYPES.PREDATOR_MISSILE:
      case MOB_TYPES.TORNADO_MISSILE:
      case MOB_TYPES.TORNADO_SMALL_MISSILE:
      case MOB_TYPES.PROWLER_MISSILE:
        this.setupStandardMissileSprite();
        break;

      case MOB_TYPES.GOLIATH_MISSILE:
        this.setupGoliathMissileSprite();
        break;

      case MOB_TYPES.COPTER_MISSILE:
        this.setupCopterMissileSprite();
        break;

      case MOB_TYPES.UPGRADE:
      case MOB_TYPES.SHIELD:
      case MOB_TYPES.INFERNO:
        this.setupCrateSprite();
        break;
    }

    this.setTeamColourOnMissiles();
  }

  private setupMissileThrusterSprites() {
    const isHitshapes = isHitshapesTheme();
    if (!isHitshapes) {
      this.sprites.thrusterGlow = texturesInit('thrusterGlowSmall', {
        layer: 'projectiles',
      });
      this.sprites.smokeGlow = texturesInit('smokeGlow', {
        layer: 'projectiles',
      });
    }
    this.sprites.thruster = texturesInit('missileThruster');
  }

  private configureThrusterEffects(
    thrusterScale: [number, number],
    thrusterAlpha: number,
    smokeScale: [number, number],
    smokeAlpha: number,
  ) {
    if (this.sprites.thrusterGlow) {
      this.sprites.thrusterGlow.scale.set(thrusterScale[0], thrusterScale[1]);
      this.sprites.thrusterGlow.alpha = thrusterAlpha;
    }
    if (this.sprites.smokeGlow) {
      this.sprites.smokeGlow.scale.set(smokeScale[0], smokeScale[1]);
      this.sprites.smokeGlow.alpha = smokeAlpha;
    }
  }

  private setupStandardMissileSprite() {
    const isHitshapes = isHitshapesTheme();
    this.sprites.sprite = texturesInit('missile', isHitshapes ? { scale: [0.2, 0.15] } : undefined);
    this.sprites.shadow = texturesInit('missileShadow', {
      scale: [0.25, 0.2],
    });
    this.configureThrusterEffects([3, 2], 0.2, [1.5, 3], 0.75);
  }

  private setupGoliathMissileSprite() {
    const isHitshapes = isHitshapesTheme();
    this.sprites.sprite = texturesInit('missileFat', isHitshapes ? { scale: [0.2, 0.2] } : undefined);
    this.sprites.shadow = texturesInit('missileShadow', {
      scale: [0.5, 0.25],
    });
    this.configureThrusterEffects([4, 3], 0.25, [2.5, 3], 0.75);
  }

  private setupCopterMissileSprite() {
    const isHitshapes = isHitshapesTheme();
    this.sprites.sprite = texturesInit('missileSmall', {
      scale: isHitshapes ? [0.2, 0.2] : [0.28, 0.2],
    });
    this.sprites.shadow = texturesInit('missileShadow', {
      scale: [0.18, 0.14],
    });
    this.configureThrusterEffects([3, 2], 0.2, [1, 2], 0.75);
  }

  private setupCrateSprite() {
    const textureName = CRATE_TEXTURE_BY_MOB_TYPE[this.type]!;
    this.state.baseScale = 0.33;
    this.state.baseScaleShadow = (2.4 / SHADOW_SCALING) * 0.33;
    this.sprites.sprite = texturesInit(textureName, {
      scale: this.state.baseScale,
    });
    this.sprites.shadow = texturesInit('crateShadow', {
      scale: this.state.baseScaleShadow,
    });
  }

  /** Applies team-based color tints to missile sprites in CTF mode. */
  setTeamColourOnMissiles() {
    if (session.gameType !== GAME_TYPES.CTF) {
      return;
    }
    if (!this.missile) {
      return;
    }

    const team = this.determineMissileTeam();
    this.applyTintToMissileSprites(this.getTeamColor(team), this.getTeamThrusterColor(team));
  }

  /** Uses owner's team if available, otherwise opposite of current player's team. */
  private determineMissileTeam() {
    if (this.ownerId !== undefined) {
      const owner = playersGet(this.ownerId);
      if (owner) {
        return owner.team;
      }
    }

    const me = playersGetMe();
    const myTeam = me ? me.team : 1;
    return myTeam === 1 ? 2 : 1;
  }

  private getTeamColor(team: TeamId) {
    if (isHitshapesTheme()) {
      return team === 1 ? HITSHAPES_TEAM_1_COLOR : HITSHAPES_TEAM_2_COLOR;
    }
    return team === 1 ? MISSILE_TEAM_1_COLOR : MISSILE_TEAM_2_COLOR;
  }

  /** Vanilla tints the thruster like the body; hitshapes lightens it. */
  private getTeamThrusterColor(team: TeamId) {
    if (isHitshapesTheme()) {
      return team === 1 ? HITSHAPES_TEAM_1_THRUSTER : HITSHAPES_TEAM_2_THRUSTER;
    }
    return team === 1 ? MISSILE_TEAM_1_COLOR : MISSILE_TEAM_2_COLOR;
  }

  private applyTintToMissileSprites(color: number, thrusterColor: number) {
    if (this.sprites.smokeGlow) {
      this.sprites.smokeGlow.tint = thrusterColor;
    }
    if (this.sprites.sprite) {
      this.sprites.sprite.tint = color;
    }
    if (this.sprites.thruster) {
      this.sprites.thruster.tint = thrusterColor;
    }
  }

  despawn(despawnType: MOB_DESPAWN_TYPES) {
    this.state.inactive = true;
    this.state.despawnTicker = 0;

    if (this.crate) {
      this.despawnCrate(despawnType);
      return;
    }

    this.despawnMissile();
  }

  /** Plays a powerup sound if the crate was picked up (not just upgraded). */
  private despawnCrate(despawnType: MOB_DESPAWN_TYPES) {
    this.state.despawnType = despawnType;

    const isPickup = despawnType === MOB_DESPAWN_TYPES.PICKUP;
    const isPowerup = this.type !== MOB_TYPES.UPGRADE;

    if (isPickup && isPowerup) {
      soundPowerup(this.type, this.pos);
    }
  }

  private despawnMissile() {
    this.hideThrusterSprites();
    this.accel.x = 0;
    this.accel.y = 0;
    if (this.missile) {
      soundClearThruster(this.thrusterName);
    }
  }

  // Both visible and renderable set false — renderable removes from render traversal
  private hideThrusterSprites() {
    if (this.sprites.thruster) {
      this.sprites.thruster.visible = false;
      this.sprites.thruster.renderable = false;
    }
    if (this.sprites.thrusterGlow) {
      this.sprites.thrusterGlow.visible = false;
      this.sprites.thrusterGlow.renderable = false;
    }
    if (this.sprites.smokeGlow) {
      this.sprites.smokeGlow.visible = false;
      this.sprites.smokeGlow.renderable = false;
    }
  }

  /** Removes the sprites and silences the thruster. Mobs adds the explosion when the server asked for one. */
  destroy() {
    if (this.missile) {
      this.destroyMissileSprites();
      soundClearThruster(this.thrusterName);
    } else if (this.crate) {
      this.destroyCrateSprites();
    }
  }

  // Pixi's destroy() also removes a sprite from its layer
  private destroyMissileSprites() {
    const { sprite, shadow, thruster, thrusterGlow, smokeGlow } = this.sprites;
    sprite?.destroy();
    shadow?.destroy();
    thruster?.destroy();
    thrusterGlow?.destroy();
    smokeGlow?.destroy();
  }

  private destroyCrateSprites() {
    const { sprite, shadow } = this.sprites;
    sprite?.destroy();
    shadow?.destroy();
  }

  network(
    msg: PlayerFireProjectile | ServerMobUpdateMessage | ServerMobUpdateStationaryMessage | EventRepelMob,
    ownerId?: number,
  ) {
    this.lastPacket = networkTiming.timeNetwork;
    if ('c' in msg && msg.c === SERVER_PACKETS.MOB_UPDATE) {
      this.reducedFactor = networkReducedFactor();
    }
    this.pos.x = msg.posX;
    this.pos.y = msg.posY;

    // Only the stationary update carries no kinematics
    if ('speedX' in msg) {
      this.speed.set(msg.speedX, msg.speedY);
      this.accel.set(msg.accelX, msg.accelY);
    }

    if (ownerId !== undefined) {
      this.ownerId = ownerId;
      this.setTeamColourOnMissiles();
    }
  }

  visible(isVisible: boolean) {
    // Skip if same visibility AND consistent cull state
    const skip = isVisible === this.visibility && isVisible !== this.culled;
    if (skip) {
      return;
    }

    if (this.sprites.sprite) {
      this.sprites.sprite.visible = isVisible;
    }
    if (this.sprites.shadow) {
      this.sprites.shadow.visible = isVisible;
    }

    if (this.missile) {
      if (this.sprites.thruster) {
        this.sprites.thruster.visible = isVisible;
      }
      if (this.sprites.thrusterGlow) {
        this.sprites.thrusterGlow.visible = isVisible;
      }
      // The original left the smoke glow visible while culled; hiding it too looks the same and draws less
      if (this.sprites.smokeGlow) {
        this.sprites.smokeGlow.visible = isVisible;
      }
    }

    this.visibility = isVisible;
  }

  visibilityUpdate() {
    this.culled = !graphicsInScreen(this.pos, 128);
    this.visible(!this.culled);
  }

  update(elapsedFrames: number) {
    this.visibilityUpdate();

    // Network jitter reduction
    if (this.reducedFactor !== null) {
      elapsedFrames -= this.reducedFactor;
      this.reducedFactor = null;

      if (elapsedFrames <= 0) {
        return;
      }
    }

    const simulationSteps = elapsedFrames > 0.51 ? Math.round(elapsedFrames) : 1;
    const timePerStep = elapsedFrames / simulationSteps;

    for (let step = 0; step < simulationSteps; step++) {
      if (this.stationary) {
        this.clientCalcs(timePerStep);
      } else {
        this.updateMissilePhysics(timePerStep);
      }
    }

    if (this.missile && !this.state.inactive) {
      soundUpdateThruster(this);
    }

    // StarMash parity: a despawned missile snaps out (~5 frames at 60fps)
    // instead of vanilla's ~1.7s full-alpha coast with a late fade — the
    // coast reads as fields of stale dots in hitshapes, where the bare
    // sprite is the whole missile. Physics still glides until the despawn
    // ticker expires, so smoke tail and deletion timing are unchanged.
    // StarMash fades 0.2/render-frame unscaled; we scale by elapsedFrames so
    // high-refresh displays match the 60fps look. We also fade the shadow
    // (StarMash doesn't — its shadow coasts at full alpha; invisible there
    // only because hitshapes hides the shadows layer).
    if (this.missile && this.state.inactive) {
      const fade = 0.2 * elapsedFrames;
      if (this.sprites.sprite && this.sprites.sprite.alpha > 0) {
        this.sprites.sprite.alpha = Math.max(0, this.sprites.sprite.alpha - fade);
      }
      if (this.sprites.shadow && this.sprites.shadow.alpha > 0) {
        this.sprites.shadow.alpha = Math.max(0, this.sprites.shadow.alpha - fade);
      }
    }
  }

  /** Trapezoid integration for velocity/position, then map wrapping. */
  private updateMissilePhysics(deltaTime: number) {
    const oldSpeedX = this.speed.x;
    const oldSpeedY = this.speed.y;

    this.speed.x += this.accel.x * deltaTime;
    this.speed.y += this.accel.y * deltaTime;

    const currentSpeed = this.speed.length();
    if (currentSpeed > this.maxSpeed) {
      this.speed.multiply(this.maxSpeed / currentSpeed);
    }

    if (this.state.inactive) {
      this.speed.multiply(1 - 0.03 * deltaTime);
    }

    this.pos.x += deltaTime * oldSpeedX + 0.5 * (this.speed.x - oldSpeedX) * deltaTime;
    this.pos.y += deltaTime * oldSpeedY + 0.5 * (this.speed.y - oldSpeedY) * deltaTime;

    this.wrapMapBoundaries();
    this.clientCalcs(deltaTime);
  }

  private wrapMapBoundaries() {
    if (this.pos.x < -MAP_CENTER_X) {
      this.pos.x += MAP_WIDTH;
    }
    if (this.pos.x > MAP_CENTER_X) {
      this.pos.x -= MAP_WIDTH;
    }

    if (this.pos.y < -MAP_CENTER_Y) {
      this.pos.y += MAP_HEIGHT;
    }
    if (this.pos.y > MAP_CENTER_Y) {
      this.pos.y -= MAP_HEIGHT;
    }
  }

  clientCalcs(deltaTime: number) {
    if (this.forDeletion) {
      return;
    }

    this.state.luminosity -= 0.075 * deltaTime;
    if (this.state.luminosity < 0) {
      this.state.luminosity = 0;
    }

    if (this.missile) {
      this.updateMissileRendering(deltaTime);
    } else if (this.crate) {
      this.updateCrateRendering(deltaTime);
    }
  }

  private updateMissileRendering(deltaTime: number) {
    let visibilityFactor = 1;

    if (this.state.inactive) {
      this.state.despawnTicker += 0.01 * deltaTime;

      // Vanilla alpha-faded sprite + shadow here once the ticker passed
      // 0.75. StarMash removed that in favour of the fast fade in update();
      // keeping it would resurrect the already-invisible sprite at 0.75.

      if (this.state.despawnTicker > 1) {
        this.forDeletion = true;
        return;
      }

      visibilityFactor = clamp(1 - this.state.despawnTicker, 0.3, 1);
    }

    if (!this.culled && visibilityFactor > 0.3) {
      this.updateMissileRotation(deltaTime);
      particlesMissileSmoke(this, this.exhaust, visibilityFactor);
    }
  }

  private updateMissileRotation(deltaTime: number) {
    const targetRotation = this.speed.angle() + Math.PI;

    // Normalize rotation difference to handle 2*PI wrapping
    if (targetRotation - this.spriteRot >= Math.PI) {
      this.spriteRot += TWO_PI;
    } else if (this.spriteRot - targetRotation > Math.PI) {
      this.spriteRot -= TWO_PI;
    }

    this.spriteRot = converge(this.spriteRot, targetRotation, 0.1 * deltaTime);
  }

  private updateCrateRendering(deltaTime: number) {
    if (!this.state.inactive) {
      return;
    }

    this.state.despawnTicker += 0.05 * deltaTime;
    if (this.state.despawnTicker > 1) {
      this.forDeletion = true;
    }
  }

  updateGraphics() {
    if (this.look !== undefined) {
      this.updateMissileGraphics(this.look);
    } else if (this.crate) {
      this.updateCrateGraphics();
    }
  }

  private updateMissileGraphics(look: MissileLook) {
    if (!this.sprites.sprite || !this.sprites.shadow) {
      return;
    }

    const shadowPos = graphicsShadowCoords(this.pos);
    const thrusterPulse = oscillator(0.1, 0.5, this.randomness);
    const glowFlicker = oscillator(0.15, 10, this.randomness);

    graphicsTransform(this.sprites.sprite, this.pos.x, this.pos.y, this.spriteRot);
    graphicsTransform(this.sprites.shadow, shadowPos.x, shadowPos.y, this.spriteRot);

    // Thruster effects sit behind the missile, along its heading
    const backX = Math.sin(-this.spriteRot);
    const backY = Math.cos(-this.spriteRot);
    const thrusterX = this.pos.x + backX * (this.exhaust + 20);
    const thrusterY = this.pos.y + backY * (this.exhaust + 20);

    const glowAlpha = (0.5 * this.state.luminosity + 0.2) * glowFlicker;
    if (this.sprites.thrusterGlow) {
      graphicsTransform(this.sprites.thrusterGlow, thrusterX, thrusterY, null, null, null, glowAlpha);
    }

    if (this.sprites.smokeGlow) {
      graphicsTransform(this.sprites.smokeGlow, thrusterX, thrusterY, this.spriteRot);
    }

    if (this.sprites.thruster) {
      graphicsTransform(
        this.sprites.thruster,
        this.pos.x + backX * this.exhaust,
        this.pos.y + backY * this.exhaust,
        this.spriteRot,
        look.thruster[0] * thrusterPulse,
        look.thruster[1] * thrusterPulse,
      );
    }
  }

  private updateCrateGraphics() {
    if (!this.sprites.sprite || !this.sprites.shadow) {
      return;
    }

    const shadowPos = graphicsShadowCoords(this.pos);

    // An expired crate shrinks away; a picked-up one bursts outward
    let scale =
      this.state.despawnType === MOB_DESPAWN_TYPES.EXPIRED
        ? 1 - this.state.despawnTicker
        : 1 + 2 * this.state.despawnTicker;
    scale *= oscillator(0.08, 500, this.randomness);
    const floatOffset = 20 * (oscillator(0.08, 330, this.randomness) - 1.04);
    const fadeAlpha = 1 - this.state.despawnTicker;
    graphicsTransform(
      this.sprites.sprite,
      this.pos.x,
      this.pos.y + floatOffset,
      null,
      this.state.baseScale * scale,
      this.state.baseScale * scale,
      fadeAlpha,
    );

    graphicsTransform(
      this.sprites.shadow,
      shadowPos.x,
      shadowPos.y,
      null,
      this.state.baseScaleShadow * scale,
      this.state.baseScaleShadow * scale,
      fadeAlpha,
    );
  }
}
